// src/PartnerDashboard.jsx
import React, { useEffect, useState, useMemo } from "react";
import { db, storage } from "./firebase";
import { apiFetch, API_BASE } from "./utils/apiClient";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  getDoc,
  setDoc,  addDoc,        // ✅ جديد
} from "firebase/firestore";
import {
  Typography,
  Box,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import UploadPreview from "./components/UploadPreview";
import { PartnerShell, Card, Field } from "./partner/PartnerUI";
import { P } from "./partner/partnerTheme";
import OrderGallery from "./partner/OrderGallery";

const PartnerDashboard = () => {
  const PARTNER_ID = "art-touch";
  const PARTNER_NAME = "ارت تاتش";
  const COMMISSION_RATE = 0.15;

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [partner, setPartner] = useState(null);
  const [openProfile, setOpenProfile] = useState(false);
  const [uploading, setUploading] = useState(false);
// 🔹 مدفوعات الشريك
const [payments, setPayments] = useState([]);

// 🔹 Dialog دفع العمولة
const [payDialogOpen, setPayDialogOpen] = useState(false);
const [paymentType, setPaymentType] = useState(""); // full | partial
const [paymentMethod, setPaymentMethod] = useState(""); // cash | transfer
const [paymentAmount, setPaymentAmount] = useState("");
const [receiptFile, setReceiptFile] = useState(null);
const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    fetchPartner();
    fetchOrders();
      fetchPayments(); // ✅ جديد
  }, []);

  const fetchPartner = async () => {
    const partnerRef = doc(db, "partners", PARTNER_ID);
    const snap = await getDoc(partnerRef);

    if (snap.exists()) {
      setPartner(snap.data());
    } else {
      const defaultData = {
        name: PARTNER_NAME,
        phone: "",
        website: "",
        notes: "",
        logoUrl: "",
        joinedAt: new Date().toISOString(),
      };
      await setDoc(partnerRef, defaultData);
      setPartner(defaultData);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const storageRef = ref(storage, `partners/${PARTNER_ID}/logo.png`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      await setDoc(
        doc(db, "partners", PARTNER_ID),
        { ...partner, logoUrl: downloadURL },
        { merge: true }
      );
      setPartner((prev) => ({ ...prev, logoUrl: downloadURL }));
    } catch {
      alert("خطأ أثناء رفع اللوغو");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    await setDoc(doc(db, "partners", PARTNER_ID), partner, { merge: true });
    setOpenProfile(false);
  };

  const fetchOrders = async () => {
    try {
      const q = query(
        collection(db, "orders"),
        where("source", "==", PARTNER_NAME),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } finally {
      setLoadingOrders(false);
    }
  };
const fetchPayments = async () => {
  const q = query(
    collection(db, "partnerPayments"),
    where("partnerId", "==", PARTNER_ID)
  );

  const snap = await getDocs(q);
  setPayments(snap.docs.map((d) => d.data()));
};

  // 🗑️ حذف طلب (أدمن فقط) - DELETE /orders/:id محمي بـrequireAdmin بالباك
  // اند، يحذف وثيقة الطلب فقط (طلبات البيع المباشر ما إلها قيود محاسبية)
  const isAdmin = localStorage.getItem("username") === "admin";
  const [deletingOrderId, setDeletingOrderId] = useState(null);

  const handleDeleteOrder = async (order) => {
    const ok = window.confirm(
      `هل أنت متأكد من حذف طلب "${order.customerName || "بدون اسم"}" نهائياً؟\nالحذف ما يتراجع عنه، والطلب ينشال من المبيعات والعمولة.`
    );
    if (!ok) return;

    try {
      setDeletingOrderId(order.id);
      const res = await apiFetch(`${API_BASE}/orders/${order.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text().catch(() => ""));
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
    } catch (err) {
      console.error("Error deleting partner order:", err);
      alert("❌ حدث خطأ أثناء حذف الطلب");
    } finally {
      setDeletingOrderId(null);
    }
  };

  const totalOrders = orders.length;
  const totalAmount = orders.reduce((sum, o) => sum + (o.price || 0), 0);
  const totalCommission = totalAmount * COMMISSION_RATE;
const totalPaid = payments.reduce(
  (sum, p) => sum + (p.amount || 0),
  0
);

const remainingCommission = totalCommission - totalPaid;

  const monthlyStats = useMemo(() => {
    const map = {};
    orders.forEach((order) => {
      const date = order.createdAt?.toDate?.() || new Date();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const key = `${year}-${month}`;
      if (!map[key]) map[key] = { year, month, count: 0, total: 0 };
      map[key].count++;
      map[key].total += order.price || 0;
    });
    return Object.values(map).sort(
      (a, b) => b.year - a.year || b.month - a.month
    );
  }, [orders]);

  const handleConfirmPayment = async () => {
    try {
      if (!paymentType || !paymentMethod) {
        alert("يرجى إكمال جميع الحقول");
        return;
      }

      setSavingPayment(true);

      let receiptUrl = "";
      if (paymentMethod === "transfer" && receiptFile) {
        const storageRef = ref(
          storage,
          `partnerPayments/${PARTNER_ID}/${Date.now()}_${receiptFile.name}`
        );
        await uploadBytes(storageRef, receiptFile);
        receiptUrl = await getDownloadURL(storageRef);
      }

      await addDoc(collection(db, "partnerPayments"), {
        partnerId: PARTNER_ID,
        partnerName: PARTNER_NAME,
        amount:
          paymentType === "full"
            ? remainingCommission
            : Number(paymentAmount),
        paymentType,
        method: paymentMethod,
        receiptUrl,
        createdAt: new Date(),
        createdBy: "admin",
      });

      alert("✅ تم تسجيل الدفع بنجاح");
      setPayDialogOpen(false);
      setPaymentType("");
      setPaymentMethod("");
      setPaymentAmount("");
      setReceiptFile(null);
      fetchPayments();
    } catch (err) {
      console.error(err);
      alert("❌ حدث خطأ أثناء الحفظ");
    } finally {
      setSavingPayment(false);
    }
  };

  // ===== عرض فقط =====
  const pad = (n) => String(n).padStart(2, "0");
  const money = (n) => `${Math.round(Number(n) || 0).toLocaleString()} د.ع`;

  return (
    <PartnerShell maxWidth={1100}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Avatar
          src={partner?.logoUrl}
          onClick={() => setOpenProfile(true)}
          sx={{ width: 64, height: 64, cursor: "pointer", border: `2px solid ${P.line}`, bgcolor: P.surface2, color: P.ink, fontWeight: 800 }}
        >
          AT
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 800, direction: "ltr", textAlign: "right" }}>Art Touch</Typography>
          <Typography sx={{ color: P.muted, fontSize: 14.5 }}>
            عرض تفاصيل الطلبات والمبيعات الخاصة بـ {PARTNER_NAME}
          </Typography>
        </Box>
        {isAdmin && (
          <Button variant="contained" onClick={() => setPayDialogOpen(true)} sx={{ background: P.success, "&:hover": { background: "#17623d" } }}>
            💵 دفع العمولة
          </Button>
        )}
      </Box>

      {/* ===== الإحصائيات ===== */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1.3fr" }, gap: 2, mb: 4 }}>
        <Card sx={{ p: 2.5, textAlign: "center" }}>
          <Typography sx={{ color: P.muted, fontWeight: 700, fontSize: 14 }}>عدد الطلبات الكلي</Typography>
          <Typography sx={{ fontSize: 30, fontWeight: 800, mt: 1 }}>{loadingOrders ? "…" : totalOrders}</Typography>
        </Card>
        <Card sx={{ p: 2.5, textAlign: "center" }}>
          <Typography sx={{ color: P.muted, fontWeight: 700, fontSize: 14 }}>مجموع المبيعات الكلي</Typography>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, mt: 1.25 }}>{loadingOrders ? "…" : money(totalAmount)}</Typography>
        </Card>
        <Card sx={{ p: 2.5, gridColumn: { xs: "1 / -1", md: "auto" }, display: "flex", flexDirection: "column", gap: 1 }}>
          <Row label={`قيمة العمولة (${COMMISSION_RATE * 100}%)`} value={money(totalCommission)} bold />
          <Row label="المبلغ المدفوع" value={money(totalPaid)} color={P.success} />
          <Row label="المتبقي" value={money(remainingCommission)} color={P.danger} bold />
        </Card>
      </Box>

      {/* ===== المبيعات الشهرية ===== */}
      <Typography sx={{ fontWeight: 800, fontSize: 19, mb: 1.5 }}>المبيعات الشهرية</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 4 }}>
        {monthlyStats.map((m, i) => (
          <Card key={i} sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 800 }}>
              <span style={{ direction: "ltr", display: "inline-block" }}>{m.year}-{pad(m.month)}</span>
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: P.muted, mt: 0.5 }}>{m.count} طلب</Typography>
            <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{money(m.total)}</Typography>
            <Typography sx={{ fontSize: 13, color: P.muted }}>
              العمولة {COMMISSION_RATE * 100}%: {money(m.total * COMMISSION_RATE)}
            </Typography>
          </Card>
        ))}
        {!loadingOrders && monthlyStats.length === 0 && (
          <Typography sx={{ color: P.muted, gridColumn: "1 / -1" }}>ماكو مبيعات بعد</Typography>
        )}
      </Box>

{/* ===== سجل مدفوعات الشريك (للمدير فقط) ===== */}
{localStorage.getItem("username") === "admin" && payments.length > 0 && (
  <>
    <Typography sx={{ fontWeight: 800, fontSize: 19, mb: 1.5 }}>💳 سجل مدفوعات العمولة</Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 4 }}>
      {payments.map((p, index) => {
        const d = p.createdAt?.toDate
          ? p.createdAt.toDate()
          : new Date(p.createdAt);

        const dateStr = `${d.getFullYear()}-${String(
          d.getMonth() + 1
        ).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

        return (
          <Card key={index} sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: { xs: 1, md: 3 }, alignItems: "center", fontSize: 14.5 }}>
            <span>📅 <b>{dateStr}</b></span>
            <span>💰 <b>{Number(p.amount).toLocaleString()} د.ع</b></span>
            <span>{p.paymentType === "full" ? "دفع كامل (تصفير الحساب)" : "دفع جزئي"}</span>
            <span>{p.method === "cash" ? "كاش" : "حوالة"}</span>
            {p.receiptUrl && (
              <a href={p.receiptUrl} target="_blank" rel="noopener noreferrer" style={{ color: P.ink, fontWeight: 700, textDecoration: "underline" }}>
                📎 عرض صورة الوصل
              </a>
            )}
          </Card>
        );
      })}
    </Box>
  </>
)}

      {/* ===== تفاصيل المبيعات ===== */}
      <Typography sx={{ fontWeight: 800, fontSize: 19, mb: 1.5 }}>تفاصيل المبيعات</Typography>

      {loadingOrders ? (
        <Typography sx={{ color: P.muted, py: 4, textAlign: "center" }}>جارِ التحميل...</Typography>
      ) : orders.length === 0 ? (
        <Card sx={{ p: 4, textAlign: "center", color: P.muted }}>ماكو طلبات بعد</Card>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {orders.map((o) => {
            const d = o.createdAt?.toDate ? o.createdAt.toDate() : new Date();
            const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
            const timeStr = `${pad(d.getHours())}:${pad(d.getMinutes())}`;

            return (
              <Card key={o.id} sx={{ p: 2 }}>
                {/* الاسم + السعر (+ زر حذف للأدمن) */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{o.customerName || "بدون اسم"}</Typography>
                    <Typography sx={{ fontSize: 13, color: P.muted, mt: 0.25 }}>
                      <span style={{ direction: "ltr", display: "inline-block" }}>{dateStr} | {timeStr}</span>
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    {typeof o.price === "number" && (
                      <Typography sx={{ fontWeight: 800, fontSize: 17, whiteSpace: "nowrap" }}>{money(o.price)}</Typography>
                    )}
                    {/* بيع مباشر فقط (partnerId) - الطلبات المتحوّلة من فاتورة إلها
                        قيود محاسبية وربط بالفاتورة، تنحذف من واجهة الفواتير */}
                    {isAdmin && o.partnerId === PARTNER_ID && (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={deletingOrderId === o.id}
                        onClick={() => handleDeleteOrder(o)}
                      >
                        {deletingOrderId === o.id ? "جارِ الحذف..." : "🗑️ حذف"}
                      </Button>
                    )}
                  </Box>
                </Box>

                {o.customerPhone && (
                  <Typography sx={{ fontSize: 14, mt: 1 }}>
                    رقم الزبون: <span style={{ direction: "ltr", display: "inline-block" }}>{o.customerPhone}</span>
                  </Typography>
                )}
                {o.governorate && (
                  <Typography sx={{ fontSize: 14, mt: 0.5 }}>المحافظة: {o.governorate}</Typography>
                )}

                {/* تفاصيل اللوحات (الطلبات الجديدة فقط - القديمة ما عدها boards) */}
                {Array.isArray(o.boards) && o.boards.length > 0 && (
                  <Box sx={{ mt: 1.25, p: 1.25, borderRadius: "10px", background: P.surface2, fontSize: 14 }}>
                    {o.boards.map((b, index) => (
                      <Typography key={index} sx={{ fontSize: 14, py: 0.25 }}>
                        🖼️ لوحة {index + 1}: <span style={{ direction: "ltr", display: "inline-block" }}>{b.lengthCm} × {b.widthCm}</span> سم
                        {b.priceType && <> ({b.priceType})</>} | العدد: {b.quantity} |{" "}
                        <b>{Number(b.price || 0).toLocaleString()} د.ع</b>
                      </Typography>
                    ))}
                    {o.discountPercent > 0 && (
                      <Typography sx={{ fontSize: 13.5, color: P.muted, mt: 0.5 }}>
                        المجموع الأولي: {Number(o.subtotal || 0).toLocaleString()} د.ع | الخصم:{" "}
                        {o.discountPercent}% ({Number(o.discountAmount || 0).toLocaleString()} د.ع)
                      </Typography>
                    )}
                  </Box>
                )}

                {/* ملف الفاتورة PDF */}
                <OrderGallery order={o} />
              </Card>
            );
          })}
        </Box>
      )}

      {/* Profile dialog */}
      <Dialog open={openProfile} onClose={() => setOpenProfile(false)} dir="rtl" fullWidth maxWidth="xs">
        <DialogTitle sx={{ textAlign: "center" }}>ملف الشريك</DialogTitle>

        <DialogContent>
          <Box textAlign="center" mb={2}>
            <Avatar
              src={partner?.logoUrl || ""}
              sx={{ width: 96, height: 96, margin: "auto", border: `2px solid ${P.line}` }}
            />
            <Button variant="outlined" component="label" size="small" sx={{ mt: 1.5 }} disabled={uploading}>
              {uploading ? "جاري الرفع..." : "تغيير الصورة"}
              <input hidden type="file" accept="image/*" onChange={handleLogoUpload} />
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Field label="اسم الشركة" value={partner?.name || ""} onChange={(e) => setPartner({ ...partner, name: e.target.value })} />
            <Field label="رقم الهاتف" value={partner?.phone || ""} onChange={(e) => setPartner({ ...partner, phone: e.target.value })} />
            <Field label="الموقع الإلكتروني" value={partner?.website || ""} onChange={(e) => setPartner({ ...partner, website: e.target.value })} />
            <Field label="ملاحظات" multiline rows={3} value={partner?.notes || ""} onChange={(e) => setPartner({ ...partner, notes: e.target.value })} />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant="outlined" onClick={() => setOpenProfile(false)} sx={{ flex: 1 }}>إلغاء</Button>
          <Button variant="contained" onClick={handleSaveProfile} sx={{ flex: 1 }}>حفظ</Button>
        </DialogActions>
      </Dialog>

      {/* دفع العمولة (أدمن) */}
      <Dialog open={payDialogOpen} onClose={() => setPayDialogOpen(false)} fullWidth maxWidth="sm" dir="rtl">
        <DialogTitle>💵 دفع عمولة الشريك</DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 2, color: P.muted }}>
            المتبقي من العمولة: <b style={{ color: P.ink }}>{money(remainingCommission)}</b>
          </Typography>

          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>نوع الدفع</Typography>
          <RadioGroup value={paymentType} onChange={(e) => setPaymentType(e.target.value)} sx={{ mb: 1.5 }}>
            <FormControlLabel value="full" control={<Radio />} label="دفع كامل (تصفير الحساب)" />
            <FormControlLabel value="partial" control={<Radio />} label="دفع جزء من العمولة" />
          </RadioGroup>

          {paymentType === "partial" && (
            <Field label="مبلغ الدفع" type="number" sx={{ mb: 2 }} value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} />
          )}

          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>طريقة الدفع</Typography>
          <RadioGroup row value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} sx={{ mb: 1.5 }}>
            <FormControlLabel value="cash" control={<Radio />} label="كاش" />
            <FormControlLabel value="transfer" control={<Radio />} label="حوالة" />
          </RadioGroup>

          {paymentMethod === "transfer" && (
            <Button variant="outlined" component="label" fullWidth sx={{ borderStyle: "dashed" }}>
              📤 رفع صورة الوصل
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => {
                  setReceiptFile(e.target.files[0] || null);
                  e.target.value = "";
                }}
              />
            </Button>
          )}
          {paymentMethod === "transfer" && (
            <UploadPreview
              files={receiptFile ? [receiptFile] : []}
              onChange={(list) => setReceiptFile(list[0] || null)}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant="outlined" onClick={() => setPayDialogOpen(false)} sx={{ flex: 1 }}>إلغاء</Button>
          <Button
            variant="contained"
            disabled={savingPayment}
            onClick={handleConfirmPayment}
            sx={{ flex: 1, background: P.success, "&:hover": { background: "#17623d" } }}
          >
            {savingPayment ? "جارِ الحفظ..." : "تأكيد الدفع"}
          </Button>
        </DialogActions>
      </Dialog>
    </PartnerShell>
  );
};

const Row = ({ label, value, color, bold }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, fontSize: 14.5 }}>
    <span style={{ color: P.muted }}>{label}</span>
    <span style={{ fontWeight: bold ? 800 : 700, color: color || P.ink, whiteSpace: "nowrap" }}>{value}</span>
  </Box>
);

export default PartnerDashboard;
